// Cost is shown in unified CNY (the team's reporting currency) with the native
// amount (USD for LLM, RMB for the CN audio/image plane) kept alongside for
// transparency. The CNY value comes from the backend (cost_cny), so the rate is a
// single source — never re-applied here and never coupled to the UI locale.
import { AlertCircle } from 'lucide-react';
import { useI18n } from '@/i18n';
import { Loading } from '@/components/common';
import {
  useCostByModel,
  useCostByAgent,
  useCostTotals,
} from '@/features/reports/hooks/useCostCenter';
import type { ModelCost, AgentCost } from '@/lib/api/costCenter';
import { StatsCard } from './StatsCard';

function nativeSymbol(currency: string): string {
  return currency === 'USD' ? '$' : currency === 'RMB' || currency === 'CNY' ? '¥' : '';
}

function formatNative(value: number, currency: string): string {
  const symbol = nativeSymbol(currency);
  return `${symbol}${value.toFixed(4)}${symbol ? '' : ` ${currency}`}`;
}

function formatNativeBreakdown(nativeCosts: Record<string, number>): string {
  const entries = Object.entries(nativeCosts);
  if (entries.length === 0) {
    return '-';
  }
  return entries.map(([currency, cost]) => formatNative(cost, currency)).join(' + ');
}

function formatCurrencyBasis(nativeCosts: Record<string, number>, currency: string | null): string {
  const currencies = Object.keys(nativeCosts);
  if (currencies.length > 0) {
    return currencies.join(' + ');
  }
  return currency ?? '-';
}

function formatCny(value: number): string {
  return `¥${value.toFixed(2)}`;
}

function formatInt(value: number): string {
  return value.toLocaleString();
}

interface CostCenterPanelProps {
  startDate: string;
  endDate: string;
  excludeTeam: boolean;
}

export function CostCenterPanel({ startDate, endDate, excludeTeam }: CostCenterPanelProps) {
  const { t } = useI18n();
  const opts = { startDate, endDate, excludeTeam };
  const totalsQuery = useCostTotals(opts);
  const byModelQuery = useCostByModel(opts);
  const byAgentQuery = useCostByAgent(opts);

  const isLoading = totalsQuery.isLoading || byModelQuery.isLoading || byAgentQuery.isLoading;
  const isError = totalsQuery.isError || byModelQuery.isError || byAgentQuery.isError;
  const error = totalsQuery.error || byModelQuery.error || byAgentQuery.error;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading message={t('reportsPage.loading')} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
        <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-lg font-semibold text-red-900 mb-1">{t('reportsPage.loadFailed')}</h3>
          <p className="text-sm text-red-700">
            {error instanceof Error ? error.message : t('reportsPage.fetchFailed')}
          </p>
        </div>
      </div>
    );
  }

  const totals = totalsQuery.data ?? {};
  const models = byModelQuery.data ?? [];
  const agents = byAgentQuery.data ?? [];
  const nativeEntries = Object.entries(totals);
  // Unified CNY grand total: sum the per-model CNY (every ledger row appears once
  // under by-model), so the two billing planes (USD + RMB) collapse to one ¥ figure.
  const cnyTotal = models.reduce((sum, m) => sum + m.cost_cny, 0);

  return (
    <div className="space-y-6">
      {/* Totals: unified CNY headline + native breakdown per currency */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard
          label={t('reportsPage.costCenter.totalCostCny')}
          value={models.length === 0 ? t('reportsPage.costCenter.noData') : formatCny(cnyTotal)}
          subtitle={t('reportsPage.costCenter.convertedCostFormula')}
          variant="profit"
        />
        {nativeEntries.map(([currency, cost]) => (
          <StatsCard
            key={currency}
            label={`${t('reportsPage.costCenter.nativeCurrencyTotal')} (${currency})`}
            value={formatNative(cost, currency)}
            subtitle={t('reportsPage.costCenter.nativeCurrencyHint')}
            variant="default"
          />
        ))}
      </div>

      {/* Per-model cost */}
      <CostTable
        title={t('reportsPage.costCenter.byModel')}
        firstColLabel={t('reportsPage.costCenter.model')}
        emptyText={t('reportsPage.costCenter.noData')}
        rows={models.map((m: ModelCost) => ({
          key: `${m.model_id}:${m.currency}`,
          name: m.model_id,
          currency: m.currency,
          calls: m.calls,
          tokens: m.input_tokens + m.output_tokens,
          nativeCosts: m.native_costs,
          costCny: m.cost_cny,
        }))}
        showTokens
        t={t}
      />

      {/* Per-agent cost */}
      <CostTable
        title={t('reportsPage.costCenter.byAgent')}
        firstColLabel={t('reportsPage.costCenter.agent')}
        emptyText={t('reportsPage.costCenter.noData')}
        rows={agents.map((a: AgentCost) => ({
          key: `${a.agent_type ?? 'unattributed'}:${a.currency}`,
          name: a.agent_type ?? t('reportsPage.costCenter.unattributed'),
          currency: a.currency,
          calls: a.calls,
          tokens: null,
          nativeCosts: a.native_costs,
          costCny: a.cost_cny,
        }))}
        showTokens={false}
        t={t}
      />
    </div>
  );
}

interface CostRow {
  key: string;
  name: string;
  currency: string | null;
  calls: number;
  tokens: number | null;
  nativeCosts: Record<string, number>;
  costCny: number;
}

interface CostTableProps {
  title: string;
  firstColLabel: string;
  emptyText: string;
  rows: CostRow[];
  showTokens: boolean;
  t: (key: string) => string;
}

function CostTable({ title, firstColLabel, emptyText, rows, showTokens, t }: CostTableProps) {
  return (
    <div className="bg-white rounded-xl p-5 border border-gray-200">
      <h4 className="text-sm font-medium text-gray-600 mb-3">{title}</h4>
      {rows.length === 0 ? (
        <div className="text-sm text-gray-400 py-6 text-center">{emptyText}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="py-2 pr-4 font-medium">{firstColLabel}</th>
                <th className="py-2 px-4 font-medium">{t('reportsPage.costCenter.currency')}</th>
                <th className="py-2 px-4 font-medium text-right">{t('reportsPage.costCenter.calls')}</th>
                {showTokens && (
                  <th className="py-2 px-4 font-medium text-right">{t('reportsPage.costCenter.tokens')}</th>
                )}
                <th className="py-2 px-4 font-medium text-right">{t('reportsPage.costCenter.nativeCost')}</th>
                <th className="py-2 pl-4 font-medium text-right">{t('reportsPage.costCenter.costCny')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 pr-4 font-mono text-gray-900">{r.name}</td>
                  <td className="py-2 px-4 text-gray-500">{formatCurrencyBasis(r.nativeCosts, r.currency)}</td>
                  <td className="py-2 px-4 text-right text-gray-700">{formatInt(r.calls)}</td>
                  {showTokens && (
                    <td className="py-2 px-4 text-right text-gray-700">
                      {r.tokens !== null ? formatInt(r.tokens) : '-'}
                    </td>
                  )}
                  <td className="py-2 px-4 text-right text-gray-400">{formatNativeBreakdown(r.nativeCosts)}</td>
                  <td className="py-2 pl-4 text-right font-semibold text-gray-900">{formatCny(r.costCny)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
